/**
 * Markdown 本文の表示。
 *
 * サニタイズ済みの HTML を `@app/markdown/render` から受け取って流し込む。
 * ここで独自にエスケープや加工をしない（レンダラを一箇所に保つ。docs/03 §6）。
 *
 * 本文中の内部リンク（/notes/<id>）はページ全体の再読み込みを避けてルータで遷移する。
 */

import { useCallback, useMemo, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { renderMarkdown, renderMarkdownExcerpt } from '@app/markdown/render';

export interface MarkdownViewProps {
  source: string;
  /** 指定すると先頭 N 行だけを描画する（折りたたみ表示用）。 */
  maxLines?: number;
  className?: string;
}

export function MarkdownView({ source, maxLines, className }: MarkdownViewProps) {
  const navigate = useNavigate();

  const html = useMemo(
    () => (maxLines ? renderMarkdownExcerpt(source, maxLines).html : renderMarkdown(source)),
    [source, maxLines],
  );

  const onClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const anchor = target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/notes/')) return;
      event.preventDefault();
      navigate(href);
    },
    [navigate],
  );

  return (
    <div
      className={className ? `markdown ${className}` : 'markdown'}
      onClick={onClick}
      // 描画元は DOMPurify を通した HTML（@app/markdown/render）。
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
